import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
    action?: {
        label: string;
        onClick: () => void;
    };
}

interface ToastNotificationProps {
    toast: Toast;
    onRemove: (id: string) => void;
}

const ToastNotification = ({ toast, onRemove }: ToastNotificationProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onRemove(toast.id);
        }, 300);
    }, [onRemove, toast.id]);

    useEffect(() => {
        // Animazione di entrata
        setTimeout(() => setIsVisible(true), 10);

        // Auto-dismiss
        if (toast.duration !== 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, toast.duration || 3000);

            return () => clearTimeout(timer);
        }
    }, [toast.duration, handleClose]);

    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return <CheckCircle className="text-green-500" size={20} />;
            case 'error':
                return <AlertCircle className="text-red-500" size={20} />;
            case 'warning':
                return <AlertTriangle className="text-yellow-500" size={20} />;
            case 'info':
                return <Info className="text-blue-500" size={20} />;
            default:
                return <Info className="text-gray-500" size={20} />;
        }
    };

    const getBackgroundColor = () => {
        switch (toast.type) {
            case 'success':
                return 'bg-green-900 border-green-500';
            case 'error':
                return 'bg-red-900 border-red-500';
            case 'warning':
                return 'bg-yellow-900 border-yellow-500';
            case 'info':
                return 'bg-blue-900 border-blue-500';
            default:
                return 'bg-gray-900 border-gray-500';
        }
    };

    return (
        <div
            className={`
        transform transition-all duration-300 ease-in-out mb-2
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${isExiting ? 'scale-95' : 'scale-100'}
      `}
        >
            <div className={`
        ${getBackgroundColor()}
        border-l-4 rounded-lg shadow-lg p-4 min-w-80 max-w-md
      `}>
                <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                        {getIcon()}
                    </div>

                    <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm">
                            {toast.title}
                        </h4>
                        {toast.message && (
                            <p className="text-gray-300 text-sm mt-1">
                                {toast.message}
                            </p>
                        )}

                        {toast.action && (
                            <button
                                onClick={toast.action.onClick}
                                className="mt-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                {toast.action.label}
                            </button>
                        )}
                    </div>

                    <button
                        onClick={handleClose}
                        className="flex-shrink-0 text-gray-400 hover:text-white transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Hook per gestire i toast
export const useToast = () => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = (toast: Omit<Toast, 'id'>) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const newToast: Toast = { ...toast, id };
        setToasts(prev => [...prev, newToast]);
        return id;
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const showSuccess = (title: string, message?: string, duration?: number) => {
        return addToast({ type: 'success', title, message, duration });
    };

    const showError = (title: string, message?: string, duration?: number) => {
        return addToast({ type: 'error', title, message, duration });
    };

    const showWarning = (title: string, message?: string, duration?: number) => {
        return addToast({ type: 'warning', title, message, duration });
    };

    const showInfo = (title: string, message?: string, duration?: number) => {
        return addToast({ type: 'info', title, message, duration });
    };

    return {
        toasts,
        addToast,
        removeToast,
        showSuccess,
        showError,
        showWarning,
        showInfo
    };
};

// Container per i toast
interface ToastContainerProps {
    toasts: Toast[];
    onRemove: (id: string) => void;
}

export const ToastContainer = ({ toasts, onRemove }: ToastContainerProps) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-[9999] space-y-2">
            {toasts.map(toast => (
                <ToastNotification
                    key={toast.id}
                    toast={toast}
                    onRemove={onRemove}
                />
            ))}
        </div>
    );
};

export default ToastNotification;